import React from 'react';
import './app-content.scss';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Dashboard } from 'src/components/features/dashboard/dashboard';
import { UnauthorizedComponent } from '../../unauthorized/unauthorized-component';
import { AppContent } from './app-content';
import { DashboardBronze } from '../../bronze/dashboard-bronze';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../store';
import { Protection } from '../../features/protection/protection';
import { GeoFiltering } from '../../features/geo-filtering/geo-filtering';
import { CategoryList } from '../../features/web-filtering/category-list/category-list';
import { Domains } from '../../features/web-filtering/domains/domains';
import { AppsPolicy } from '../../features/apps-services/apps-policy';
import { ServicesPolicy } from '../../features/services/services-policy';
import { ProtectionPlus } from '../../features/protection-plus/protection-plus';
import { FirewallRules } from '../../features/firewall-policy/firewall-rules';
import { Addresses } from '../../features/inventory/addresses/addresses';
import { AddressGroups } from '../../features/inventory/groups/address-groups';
import { ServicesInventory } from '../../features/inventory/services/services-inventory';
import { LogsPage } from '../../features/reports-and-logs/logs/logs-page';
import { ActivityPage } from '../../features/activity/activity-page';
import { Insights } from '../../features/reports-and-logs/insights';
import { IPControl } from '../../features/ip-control/ip-control';
import { WebFiltering } from '../../features/web-filtering/web-filtering';
import { RulesViewer } from '../../features/firewall-policy/firewall-rules-table/rules-viewer';
import RuleEditor from '../../features/firewall-policy/rule-editor/rule-editor';
import { Inventory } from '../../features/inventory/inventory';
import { ReportsPage } from '../../features/reports-and-logs/new-reports/reports-page';
import ReportViewer from '../../features/reports-and-logs/new-reports/report-viewer/report-viewer';
import { IpList } from '../../features/ip-control/ip-list/ip-list';
import WizardPage from '../../features/wizard/wizard-page';
import Ecm from '../../features/ecm/ecm';
import { CustomerSelection } from '../../features/customer-selection/customer-selection';

interface AppRouterProps {
    defaultRoute: string;
}

// Absolute target: in react-router 7 a relative path inside a splat route resolves against the full URL.
const NavigateToDashboard: React.FC = () => {
    const { customerId, serviceId } = useParams();
    return <Navigate replace to={`/${customerId}/${serviceId}/dashboard`}/>;
};

export const AppRouter: React.FC<AppRouterProps> = observer(({ defaultRoute }) => {

    const { fwVersionStore, logsStore } = useStores();

    return (
        <Routes>
            <Route path="/*" element={<Navigate to={defaultRoute}/>}/>
            <Route path="/select-customer" element={<CustomerSelection/>}/>
            <Route path="/unauthorized/:errorType" element={<UnauthorizedComponent/>}/>
            <Route path=":customerId/wizard" element={<WizardPage/>}/>
            <Route path=":customerId/:serviceId/" element={<Navigate replace to="/select-customer"/>}/>
            <Route path=":customerId/:serviceId/dashboard-bronze" element={<DashboardBronze/>}/>
            <Route path=":customerId">
                <Route path="connections" element={<Ecm/>}/>
            </Route>
            <Route path=":customerId/:serviceId/*" element={<AppContent/>}>
                <Route path="dashboard" element={<Dashboard/>}/>
                <Route path="protection/*" element={<Protection/>}>
                    <Route path="version">
                        <Route path=":versionId">
                            <Route path="geo-filtering" element={<GeoFiltering/>}/>
                            <Route path="web-filtering" element={<WebFiltering/>}>
                                <Route path="categories" element={<CategoryList/>}/>
                                <Route path="domains" element={<Domains/>}/>
                                <Route index element={<Navigate replace to="categories"/>}/>
                            </Route>
                            <Route path="apps" element={<AppsPolicy/>}/>
                            <Route path="services" element={<ServicesPolicy/>}/>
                            <Route path="ip-control" element={<IPControl/>}>
                                <Route index element={<IpList/>}/>
                            </Route>
                            <Route path="protection-plus" element={<ProtectionPlus/>}/>
                            <Route path="firewall-rules" element={<FirewallRules/>}>
                                <Route path="" element={<RulesViewer/>}/>
                                <Route path="add" element={<RuleEditor/>}/>
                                <Route path="edit/:ruleId" element={<RuleEditor/>}/>
                            </Route>
                            <Route path="library" element={<Inventory/>}>
                                <Route path="addresses" element={<Addresses/>}/>
                                <Route path="address-groups" element={<AddressGroups/>}/>
                                <Route path="services" element={<ServicesInventory/>}/>
                                <Route index element={<Navigate replace to="addresses"/>}/>
                            </Route>
                            <Route index element={<Navigate replace to={fwVersionStore.protectionType}/>}/>
                        </Route>
                        <Route index element={<Navigate replace to={fwVersionStore.selectedVersionId || ''}/>}/>
                    </Route>
                    <Route index element={<Navigate replace to={'version'}/>}/>
                </Route>
                <Route path="insights" element={<Insights/>}>
                    <Route path="logs" element={<LogsPage/>}/>
                    <Route path="reports" element={<ReportsPage/>}>
                        <Route path=":reportType" element={<ReportViewer/>}/>
                    </Route>
                    <Route path="activity" element={<ActivityPage/>}/>
                    <Route index element={<Navigate replace to={logsStore.activeSubMenu.toLowerCase()} />}/>
                </Route>
                <Route path="*" element={<NavigateToDashboard/>}/>
            </Route>
        </Routes>
    );
});

